/**
 * Menú de acciones de una fila de tabla.
 *
 * Se usa en las pantallas con muchas acciones por fila (Comprobantes, Créditos…)
 * para no ensanchar la tabla: los botones con texto obligaban a desplazarse a la
 * derecha en ventanas estrechas.
 *
 * Se dibuja en un portal anclado al botón para que no lo recorte el borde de la
 * tarjeta que envuelve la tabla, y se cierra al hacer clic fuera, al desplazarse
 * o al cambiar el tamaño de la ventana.
 */

import { MoreVertical } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';

import { Button } from './button';

export interface AccionDeMenu {
  etiqueta: string;
  icono: ReactNode;
  /** Se abre en otra pestaña (por ejemplo las impresiones del comprobante). */
  href?: string;
  onClick?: () => void;
  /** Pinta la opción en rojo (anular, nota de crédito…). */
  peligrosa?: boolean;
}

const ANCHO_MENU = 208;

export function MenuAcciones({
  acciones,
  etiqueta = 'Más acciones',
}: {
  acciones: AccionDeMenu[];
  etiqueta?: string;
}) {
  const [abierto, setAbierto] = useState(false);
  const [posicion, setPosicion] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!abierto) return undefined;

    const cerrar = () => setAbierto(false);

    document.addEventListener('click', cerrar);
    window.addEventListener('scroll', cerrar, true);
    window.addEventListener('resize', cerrar);

    return () => {
      document.removeEventListener('click', cerrar);
      window.removeEventListener('scroll', cerrar, true);
      window.removeEventListener('resize', cerrar);
    };
  }, [abierto]);

  if (!acciones.length) return null;

  const medir = (evento: React.MouseEvent<HTMLButtonElement>) => {
    const caja = evento.currentTarget.getBoundingClientRect();

    setPosicion({
      top: caja.bottom + 4,
      left: Math.max(8, Math.min(caja.right - ANCHO_MENU, window.innerWidth - ANCHO_MENU - 8)),
    });
    setAbierto((previo) => !previo);
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        title={etiqueta}
        aria-label={etiqueta}
        aria-haspopup="menu"
        aria-expanded={abierto}
        onClick={(evento) => {
          evento.stopPropagation();
          medir(evento);
        }}
      >
        <MoreVertical className="size-4" aria-hidden="true" />
      </Button>

      {abierto && posicion
        ? createPortal(
            <div
              role="menu"
              style={{ top: posicion.top, left: posicion.left, width: ANCHO_MENU }}
              className="fixed z-50 rounded-card border border-ink-200 bg-white p-1 shadow-xl"
            >
              {acciones.map((accion) =>
                accion.href ? (
                  <a
                    key={accion.etiqueta}
                    role="menuitem"
                    className="flex items-center gap-2 rounded-control px-2 py-2 text-xs font-medium text-brand-700 hover:bg-brand-50"
                    href={accion.href}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {accion.icono}
                    {accion.etiqueta}
                  </a>
                ) : (
                  <button
                    key={accion.etiqueta}
                    type="button"
                    role="menuitem"
                    className={cn(
                      'flex w-full items-center gap-2 rounded-control px-2 py-2 text-left text-xs font-medium hover:bg-ink-50',
                      accion.peligrosa ? 'text-rose-600' : 'text-ink-700',
                    )}
                    onClick={accion.onClick}
                  >
                    {accion.icono}
                    {accion.etiqueta}
                  </button>
                ),
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
